/** Orchestrator: transactions → subscriptions + totals + trend series. */

import type { AnalysisResult, Subscription, Transaction } from '../types';
import { merchant } from '../merchants/normalize';
import { addOverlapFlags } from './flags';
import { evaluateGroup, type MerchantGroup } from './evaluate';
import { makeMoneyFormatter } from './format';
import { monthlySeries } from './series';

export function analyze(
  transactions: Transaction[],
  currency: string | null = null,
  today: Date = new Date(),
): AnalysisResult {
  const money = makeMoneyFormatter(currency);

  const groups = new Map<string, MerchantGroup>();
  for (const t of transactions) {
    const { key, display } = merchant(t.description);
    let g = groups.get(key);
    if (!g) {
      g = { key, display, raw: t.description, txns: [] };
      groups.set(key, g);
    }
    g.txns.push(t);
  }

  const subscriptions: Subscription[] = [];
  for (const g of groups.values()) {
    subscriptions.push(...evaluateGroup(g, today, money));
  }

  addOverlapFlags(subscriptions, money);
  subscriptions.sort((a, b) => b.monthlyCost - a.monthlyCost);

  const active = subscriptions.filter((s) => s.status === 'active');
  const monthlyCost = active.reduce((a, s) => a + s.monthlyCost, 0);

  return {
    subscriptions,
    totals: {
      monthlyCost,
      yearlyCost: monthlyCost * 12,
      activeCount: active.length,
      inactiveCount: subscriptions.length - active.length,
    },
    monthlySeries: monthlySeries(subscriptions, today),
  };
}
